import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Package, Loader2, ExternalLink } from "lucide-react";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { AssetPricingBadge } from "@/components/marketplace/AssetPricingBadge";
import { AiAssetBadge } from "@/components/marketplace/AiAssetBadge";
import { CreatorVideoFeed } from "@/components/videos/CreatorVideoFeed";

/**
 * Phase 24e · Creator public store page.
 * Route: /creator/:id/store
 */
export default function CreatorStore() {
  const { id } = useParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/marketplace/creator/${encodeURIComponent(id)}/store`);
      setStore(res);
    } catch (e) {
      setError(e?.message || "load_error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Phase 25b · SEO JSON-LD Store schema
  useEffect(() => {
    if (!store) return;
    try {
      const el = document.getElementById("creator-store-jsonld")
        || document.createElement("script");
      el.id = "creator-store-jsonld";
      el.type = "application/ld+json";
      el.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Store",
        name: `${store.creator?.name || id} — Creator Store`,
        url: window.location.href,
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Digital assets",
          itemListElement: (store.items || []).slice(0, 20).map((a, i) => ({
            "@type": "Offer",
            position: i + 1,
            itemOffered: {
              "@type": (a.ai_generated || a.creator_type === "virtual")
                ? "SoftwareApplication"
                : "Product",
              name: a.title,
            },
            price: ((a.price_eur_cents || 0) / 100).toFixed(2),
            priceCurrency: "USD",
          })),
        },
      });
      if (!el.parentNode) document.head.appendChild(el);
    } catch {}
  }, [store, id]);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)]">
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-6 md:p-10 max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="creator-store-page"
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-white hover:bg-[#EDEDED] flex items-center justify-center">
                <User className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-text-secondary mb-1">
                  Creator Store
                </div>
                <h1
                  data-testid="creator-store-name"
                  className="text-3xl md:text-4xl font-semibold tracking-tight"
                >
                  {store?.creator?.name || id.slice(0, 8)}
                </h1>
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
              </div>
            )}

            {error && (
              <div className="rounded-2xl border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/5 p-6 text-sm text-[var(--color-alert)]">
                {error}
              </div>
            )}

            {store && !loading && (
              <>
                {(() => {
                  const isVirtual = String(id || "").startsWith("ai:")
                    || (store.items || []).some((a) => a.creator_type === "virtual" || a.ai_generated);
                  const hasPaid = (store.items || []).filter(
                    (a) => a.is_paid && (a.price_eur_cents || 0) > 0
                  ).length >= 3;
                  return (
                    <>
                      {isVirtual && (
                        <div
                          data-testid="creator-store-virtual-banner"
                          className="mb-6 rounded-2xl border border-[#2A2A2A]-500/40 bg-gradient-to-r from-neutral-500/10 to-neutral-400/10 p-4 flex items-start gap-3"
                        >
                          <span className="text-2xl">🤖</span>
                          <div>
                            <div className="text-sm font-semibold text-[var(--color-text-main)] flex items-center gap-2">
                              <span
                                data-testid="creator-store-ai-badge"
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#161616]-500/20 text-[#CCCCCC]-200 border border-[#2A2A2A]-500/40"
                              >
                                AI Creator
                              </span>
                              Content generato automaticamente
                            </div>
                            <p
                              data-testid="creator-store-platform-revenue-banner"
                              className="text-xs text-text-secondary mt-1"
                            >
                              Trasparenza: 100% del ricavato di questi asset va alla piattaforma per
                              coprire i costi di generazione e moderazione AI. Nessun creator umano
                              percepisce ricavi da questi contenuti.
                            </p>
                          </div>
                        </div>
                      )}
                      {!isVirtual && hasPaid && (
                        <div
                          data-testid="creator-store-bundle-cta"
                          className="mb-6 rounded-2xl border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 p-4 text-sm text-[var(--color-success)] flex items-center gap-3"
                        >
                          🎁 <strong>Bundle discount:</strong> compra 3+ asset e risparmia il 20% ·
                          5+ per il 35%.
                        </div>
                      )}
                    </>
                  );
                })()}

                <div className="mb-4 text-xs text-text-secondary">
                  <span data-testid="creator-store-total">{store.total}</span> asset pubblicati
                </div>

                {store.items.length === 0 ? (
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center">
                    <Package className="h-10 w-10 text-text-secondary mx-auto mb-3" />
                    <p className="text-sm text-text-secondary">
                      Questo creator non ha ancora pubblicato asset a pagamento.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {store.items.map((a) => (
                      <Link
                        key={a.asset_id}
                        to={`/marketplace/asset/${a.asset_id}`}
                        data-testid={`creator-store-card-${a.asset_id}`}
                        className="group rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 hover:border-[var(--color-primary)]/60 transition-colors block"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <AssetPricingBadge
                            is_paid={a.is_paid}
                            price_eur_cents={a.price_eur_cents}
                            size="sm"
                          />
                          {(a.ai_generated || a.creator_type === "virtual") && (
                            <AiAssetBadge assetAiType={a.asset_ai_type} size="sm" />
                          )}
                        </div>
                        <h3 className="text-sm font-semibold text-[var(--color-text-main)] mb-1 line-clamp-2">
                          {a.title}
                        </h3>
                        <p className="text-[11px] text-text-secondary line-clamp-2 leading-snug">
                          {a.description || "Nessuna descrizione."}
                        </p>
                        <div className="flex items-center justify-between mt-3 text-[10px] uppercase tracking-widest text-text-secondary">
                          <span>{a.category}</span>
                          <span>{a.sales_count || 0} vendite</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                <CreatorVideoFeed creatorId={id} />
              </>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
